import { isEmpty, logger } from '@marriott/shared/mi-helper-utils';
import { getPageModel } from './helper';
import { getDataLayer } from './dataLayer';
import { RequestProps, resType } from './index.types';
import { meetingsMock } from './_localDevMocks/meetings-model';
import { weddingsMock } from './_localDevMocks/weddings-mock';
import { constants } from '../../constants/lib/constants';
import { inspect } from 'util';

const { REDIRECT_URL } = process.env;

const MOCK_SESSION_ID = '7FC3A108-DF69-5E10-B767-6D2FB461B924';

declare global {
  // eslint-disable-next-line no-var
  var loggerInstance: CallableFunction;
}

const redirect = {
  redirect: {
    permanent: false,
    destination: REDIRECT_URL,
  },
};

// Redirect user to configured page in case request is coming from getInitialProps method
const getInitialPropsRedirect = (res: resType['res'], redirectObj: typeof redirect) => {
  res.writeHead(307, { Location: redirectObj.redirect.destination });
  res.end();
};

/**
 * Method to conditionally redirect user to target page based on whether the request
 * is initialted by getInitialProps method or getServerSideProps
 * @param res Response Object
 * @param redirectObj Object containing redirection configs
 * @param IS_INTIAL_PROPS_CALL Boolean variable for branching
 * @returns
 */
const redirectUser = (res: Record<string, string>, redirectObj: typeof redirect, IS_INTIAL_PROPS_CALL: boolean) => {
  if (IS_INTIAL_PROPS_CALL) {
    getInitialPropsRedirect(res, redirectObj);
    return res;
  } else {
    return redirectObj;
  }
};

/**
 *
 * @param gssp
 * @returns props
 * This function is used to excute the calls before the page gets rendered.
 * session, interceptor, pre-process, page model, datalayer
 */
export function prePageCall(gssp: (preData: Record<string, string>) => { props: Record<string, string> }) {
  let IS_INTIAL_PROPS_CALL = false;
  return async (context: {
    resolvedUrl?: string;
    locale?: string;
    req: {
      cookies?: { sessionID: string; UserIdToken: string };
      headers: RequestProps;
      query: Record<string, unknown>;
      originalUrl: string;
      url: string;
    };
    res: Record<string, string>;
  }) => {
    try {
      const {
        req,
        res,
      }: {
        req: {
          cookies?: { sessionID: string; UserIdToken: string };
          headers: RequestProps;
          query: Record<string, unknown>;
          originalUrl: string;
          url: string;
        };
        res: Record<string, string>;
      } = context;

      // when using getInitialProps we need to use originalUrl under req object
      // when using getServertSideProps resolvedUrl can be read from context
      const resolvedUrl: string = context.resolvedUrl ?? req.originalUrl ?? req.url;

      // resolvedUrl will only be present if page is using getServerSideProps
      if (!context.resolvedUrl) {
        IS_INTIAL_PROPS_CALL = true;
      }

      global.loggerInstance = logger({
        sessionID: req?.cookies?.sessionID,
        requestID: req.headers['x-request-id'],
      }) as CallableFunction;

      const { log, pLog } = global.loggerInstance('Pre Page');
      log.debug(`Resolved Url: ${resolvedUrl}`);
      log.debug(`Locale ${context?.locale}`);
      log.debug(`Cookies ${inspect(req?.cookies)}`);
      log.debug(`req.headers ${inspect(req.headers)}`);

      const isDev = process.env['NODE_ENV'] === constants.ENV_DEV;
      const sessionIDVal = req?.cookies?.sessionID ?? MOCK_SESSION_ID;

      log.debug('Page Model call started');
      const apiStartTime = new Date().getTime();
      const currentLocale = `${req.headers['accept-language']}`;
      log.debug(`[STEP 5] currentLocale: ${currentLocale}`);
      const requestId = req.headers['x-request-id'] ?? `${Date.now()}`;
      const mock = resolvedUrl.includes('meeting') ? meetingsMock : weddingsMock;
      const locale = context.locale ?? 'en';
      const model = isDev ? mock : await getPageModel(`/${locale}${resolvedUrl}`, locale, sessionIDVal, requestId); //get page model
      log.debug(`page model data - ${JSON.stringify(model)}`);

      pLog.log('Page Model call ended', apiStartTime, new Date().getTime());
      const gsspData = await gssp({}); // Run `getServerSideProps` to get page-specific data

      // in case AEM or fetch model service is down, redirect user to configured
      // redirect page. This will fix "Skip to content" link on an empty page issue.
      if (isEmpty(model)) {
        log.debug(`page model not available, redirecting to configured page - ${JSON.stringify(redirect)}`);
        return redirectUser(res, redirect, IS_INTIAL_PROPS_CALL);
      }

      log.debug('DataLayer call started');
      let dataLayer = null;
      /**
       * DataLayer Call
       */
      try {
        dataLayer = await getDataLayer(req, resolvedUrl, locale);
        log.debug(`[STEP 4] datalayer success, response ${inspect(dataLayer)}`);
      } catch (err) {
        log.error(`[STEP 4] datalayer failed, error: ${err}`);
      }
      log.debug('DataLayer call ended');

      // when using getInitialProps we should return the data without props key
      // will be passed as is to pages.

      const returnObj = {
        model,
        resolvedUrl,
        requestId,
        dataLayer,
        currentLocale,
        ...gsspData.props,
      };

      return {
        props: returnObj,
      };
    } catch (e) {
      const { log } = global.loggerInstance('Pre Page');
      log.debug('calling logger here \n\n\n\n\n', global.loggerInstance, e);

      log.error(`prepage call error occurred: ${e}`);
      try {
        const { res } = context;
        return redirectUser(res, redirect, IS_INTIAL_PROPS_CALL);
      } catch (err) {
        console.error('Error in page model catch block, response desctructuring failed', err);
      }
    }
  };
}
